import { IconThemeContribution } from '@theia/core/lib/browser/icon-theme-contribution';
import { IconTheme, IconThemeService } from '@theia/core/lib/browser/icon-theme-service';
import {
    DidChangeLabelEvent,
    LabelProvider,
    LabelProviderContribution,
    URIIconReference
} from '@theia/core/lib/browser/label-provider';
import { Disposable } from '@theia/core/lib/common/disposable';
import { Emitter } from '@theia/core/lib/common/event';
import URI from '@theia/core/lib/common/uri';
import { inject, injectable, postConstruct } from '@theia/core/shared/inversify';
import { FileStatNode } from '@theia/filesystem/lib/browser';
import { FileStat } from '@theia/filesystem/lib/common/files';
import {
    FILE_ICON_THEME_CSS,
    getFileIconClassName,
    PANEL_FILE_ICON_THEME_ID,
    resolveFileIconName
} from './file-icon-data';

@injectable()
export class FileIconTheme implements IconTheme {
    readonly id: string = PANEL_FILE_ICON_THEME_ID;
    readonly label: string = 'LVGL File Icons';
    readonly description: string = 'File icons based on the LVGL Pro Editor UI icon set';
    readonly hasFileIcons: boolean = true;
    readonly hasFolderIcons: boolean = false;
    readonly showLanguageModeIcons: boolean = false;

    activate(): Disposable {
        const styleElement: HTMLStyleElement = document.createElement('style');
        styleElement.type = 'text/css';
        styleElement.className = 'theia-icon-theme';
        styleElement.innerText = FILE_ICON_THEME_CSS;
        document.head.appendChild(styleElement);

        return Disposable.create(() => styleElement.remove());
    }
}

@injectable()
export class FileIconThemeContribution implements IconThemeContribution {
    @inject(FileIconTheme)
    protected readonly theme!: FileIconTheme;

    registerIconThemes(iconThemes: IconThemeService): void {
        iconThemes.register(this.theme);
    }
}

@injectable()
export class FileIconLabelProviderContribution implements LabelProviderContribution {
    @inject(IconThemeService)
    protected readonly iconThemeService!: IconThemeService;

    @inject(LabelProvider)
    protected readonly labelProvider!: LabelProvider;

    protected readonly onDidChangeEmitter = new Emitter<DidChangeLabelEvent>();
    readonly onDidChange = this.onDidChangeEmitter.event;

    @postConstruct()
    protected init(): void {
        this.iconThemeService.onDidChangeCurrent(() => this.fireDidChange());
    }

    canHandle(element: object): number {
        if (this.iconThemeService.current !== PANEL_FILE_ICON_THEME_ID) {
            return 0;
        }

        if (element instanceof URI && element.scheme === 'file') {
            return Number.MAX_SAFE_INTEGER;
        }

        if (URIIconReference.is(element) || FileStat.is(element) || FileStatNode.is(element)) {
            return Number.MAX_SAFE_INTEGER;
        }

        return 0;
    }

    getIcon(element: URI | URIIconReference | FileStat | FileStatNode): string | undefined {
        if (this.iconThemeService.current !== PANEL_FILE_ICON_THEME_ID) {
            return undefined;
        }

        if (URIIconReference.is(element) && element.id === 'folder') {
            return undefined;
        }

        if (FileStatNode.is(element) && element.fileStat.isDirectory) {
            return undefined;
        }

        if (FileStat.is(element) && element.isDirectory) {
            return undefined;
        }

        const fileName: string = this.labelProvider.getName(element);
        const iconName: string = resolveFileIconName(fileName);
        return getFileIconClassName(iconName);
    }

    protected fireDidChange(): void {
        this.onDidChangeEmitter.fire({ affects: () => true });
    }
}
